
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from 'react';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import Head from 'next/head';
import getT from 'next-translate/getT';
import { isServerError } from 'api/apiHelpers';
import { withForcePlanToLoggedInUnsubs } from 'context';
import { Permalink, slugifyString, utf8_decode } from 'helpers';
import { basicSEO } from 'helpers/jsx';
import Page404 from 'pages/404';
import CategorySectionPlaylist from '../../../components/CategorySections/CategorySectionPlaylist';
import Layout from '../../../components/Layout';
import { Namespace, PlaylistType } from 'enums';
import { FeaturedSection, Genre, SimilarPlayType } from '../../../typings/api';
import { StaticPathParams } from '../../../typings/staticPaths';
import { authorizeUserServerSide } from 'api/firebaseAdmin';

type Section = FeaturedSection | Genre;
interface Props {
  section: Section;
  description: string;
  headline: string;
  countryCode: string;
}

const Playlist: NextPage<Props> = ({ section, headline, description, countryCode }) => {
  if (!section) {
    return <Page404 />;
  }

  return (
    <Layout countryCode={countryCode}>
      <Head>{basicSEO(section?.name, description)}</Head>
      <CategorySectionPlaylist headline={headline} plays={section?.plays} />
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  const { apiClient, locale, countryCode } = await authorizeUserServerSide(ctx);

  try {
    const {
      query,
      params: { slug: _slug },
    } = ctx;

    const parts = _slug.split('-');
    const id = +parts[0];
    parts.shift();
    const slug = parts.join('-');

    let section: Section;
    let description: string;
    let headline: string;
    const t = await getT(locale, Namespace.Seo);
    const tCommon = await getT(locale, Namespace.Common);

    if (query.type === PlaylistType.SameGenre) {
      const play = await apiClient.getPlayDetail(id);

      if (slug !== slugifyString(play.title)) {
        return {
          redirect: {
            permanent: true,
            destination: Permalink.sameGenre(play, locale),
          },
        };
      }

      const plays = await apiClient.getSimilarPlays(play.id, {
        type: SimilarPlayType.Genre,
      });
      section = {
        plays,
        name: t('seo:playlist_performance_similar_genre_title', { performance: play.title }),
        id: null,
      };
      headline = tCommon('playlist_same_genre_title', { performance: play.title });
      description = t('seo:playlist_performance_similar_genre_description', { performance: play.title });
    } else if (query.type === PlaylistType.SameCast) {
      const play = await apiClient.getPlayDetail(id);

      if (slug !== slugifyString(play.title)) {
        return {
          redirect: {
            permanent: true,
            destination: Permalink.sameCast(play, locale),
          },
        };
      }

      const plays = await apiClient.getSimilarPlays(play.id, {
        type: SimilarPlayType.Cast,
      });
      section = {
        plays,
        name: t('seo:playlist_performance_similar_cast_title', { performance: play.title }),
        id: null,
      };
      headline = tCommon('playlist_same_cast_title', { performance: play.title });
      description = t('seo:playlist_performance_similar_cast_description', { performance: play.title });
    } else if (query.type === PlaylistType.Section) {
      section = (await apiClient.getHomepageSection(id)).section;

      if (slug !== slugifyString(section.name)) {
        return {
          redirect: {
            permanent: true,
            destination: Permalink.featuredSection(section, locale),
          },
        };
      }
      headline = tCommon('playlist_section_title', { section: section.name });
      section.name = t('seo:playlist_featured_title', { section: utf8_decode(section.name) });
      description = t('seo:playlist_featured_description', { section: section.name });
    } else if (query.type === PlaylistType.Genres) {
      if (isNaN(+id)) {
        const genres = await apiClient.getGenres();
        section = genres.find((g) => g.slug === _slug);

        if (!section) {
          throw new Error(`Malformed genre id ${id}`);
        }
      } else {
        section = await apiClient.getGenre(id);
      }

      if (slug !== slugifyString(section.name)) {
        return {
          redirect: {
            permanent: true,
            destination: Permalink.genre(section, locale),
          },
        };
      }

      headline = tCommon('common:playlist_genre_title', { genre: section?.name });
      description = t('seo:playlist_genre_description', { genre: section?.name });
      section.name = t('seo:playlist_genre_title', { genre: section?.name });
    }

    if (isServerError(section as unknown as JSON)) {
      section = null;
    }

    return {
      props: { section: section || null, description: description || null, headline: headline || null, countryCode },
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
}

export default withForcePlanToLoggedInUnsubs(Playlist);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/playlist/[slug]/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  